import * as BSON from "bson";
import { UserCustomData } from "./Realm.interfaces";

const API_URL =
  import.meta.env.VITE_DAL_URL +
  (window.origin.endsWith(".cleandns.space") ||
  window.origin.endsWith(".cleandns.app") ||
  window.origin.endsWith(".cleandns.dev") ||
  window.location.hostname === "localhost"
    ? ""
    : "/proxy");

// TODO: replace mongodb types with realm-web types

export type User = {
  id: string;
  anonymous?: boolean;
  customData: UserCustomData;
  isLoggedIn: boolean;
  mongoClient: (name: string) => Realm.Services.MongoDB;
  logOut: () => Promise<void>;
  callFunction: (name: string, args?: Record<string, any>) => Promise<any>;
  callFunctionRaw: (name: string, args?: Record<string, any>) => Promise<any>;
  refreshCustomData: () => Promise<void>;
  profile: {
    email?: string;
  };
  apiKeys: UserApiKeys;
};

export type UserApiKeys = {
  fetchAll: () => Promise<ApiKey[]>;
  create: (name: string) => Promise<ApiKey>;
  delete: (id: string) => Promise<void>;
};

export type ApiKey = {
  _id: string;
  key: string;
  name: string;
  disabled: boolean;
};

export type Credentials = { type: "anonymous" };

export const Credentials = {
  anonymous: (): Credentials => ({ type: "anonymous" }),
};

export class App {
  appId: string;
  currentUser: User | undefined;
  fetch: (
    input: RequestInfo,
    init?: {
      method: "GET" | "POST" | "PATCH" | "PUT" | "DELETE";
      headers?: Record<string, any>;
      body?: any;
    },
  ) => Promise<Response>;

  constructor(args: { id: string }) {
    this.appId = args.id;
    console.log("DAL API URL:", API_URL);

    this.fetch = async (input, init?) => {
      return await fetch(
        typeof input === "string"
          ? `${API_URL}${input}`
          : { ...input, url: `${API_URL}${input.url}` },
        init
          ? {
              ...init,
              credentials: "include",
              headers: {
                "Content-Type": "application/json",
                ...(init.headers ? init.headers : {}),
              },
              body: init.body ? BSON.EJSON.stringify(init.body) : undefined,
            }
          : {
              credentials: "include",
            },
      ).then((r) => {
        if (r.status >= 200 && r.status < 300) {
          r.json = async () => r.text().then((text) => BSON.EJSON.parse(text));
          return r;
        } else if (r.status === 401) {
          console.error("Session expired, logging out user.");
          this.currentUser = undefined;
          localStorage.removeItem(`realmCurrentUser:${this.appId}`);
          throw { message: "Session expired", code: r.status, cause: r };
        } else {
          return r
            .text()
            .then(
              (text) =>
                BSON.EJSON.parse(text) as {
                  error: string;
                  code: string | number;
                },
            )
            .then((data) => {
              throw {
                message: `Request failed with status ${r.status}: ${r.statusText} -- ${data.error}`,
                code: r.status,
                cause: r,
                data,
              };
            });
        }
      });
    };

    const localUser = localStorage.getItem(`realmCurrentUser:${this.appId}`);
    if (localUser) {
      this.currentUser = this.hydrateUser(BSON.EJSON.parse(localUser));
    } else {
      this.currentUser = undefined;
    }
  }

  async callFunction<T = any>(
    name: string,
    args: Record<string, any> = {},
  ): Promise<T> {
    return await this.fetch(`/dal/functions/${name}`, {
      method: "POST",
      body: args,
    })
      .then((res) => res.json() as Promise<T>)
      .then((r) => {
        // console.log({ functionName: name, args, result: r });
        return r;
      });
  }

  async callFunctionRaw(
    name: string,
    args: Record<string, any> = {},
  ): Promise<Response> {
    return await this.fetch(`/dal/functions/${name}`, {
      method: "POST",
      body: args,
    });
  }

  hydrateUser(data: any): User {
    const app = this;
    const user: User = {
      id: Array.isArray(data.customData.identity.stitch_id)
        ? data.customData.identity.stitch_id[0]
        : data.customData.identity.stitch_id,
      customData: data.customData,
      isLoggedIn: true,
      mongoClient: mongoClient(app),
      logOut: async () => {
        return await app
          .fetch("/session/end", {
            method: "POST",
          })
          .then(() => {
            app.currentUser = undefined;
            localStorage.removeItem(`realmCurrentUser:${app.appId}`);
          });
      },
      callFunction: app.callFunction.bind(app),
      callFunctionRaw: app.callFunctionRaw.bind(app),
      refreshCustomData: async () => {
        return await app
          .fetch("/session/check", {
            method: "GET",
          })
          .then((res) => {
            if (res.status !== 200) {
              throw new Error("Failed to refresh custom data");
            }
            return res.json();
          })
          .then((data: UserCustomData) => {
            user.customData = data;
            return;
          });
      },
      profile: {
        email: data.customData.email,
      },
      apiKeys: {
        fetchAll: async () => {
          return [user.customData.apiToken];
        },
        create: async (name: string) => {
          return {
            _id: "mocked_id",
            key: "mocked_key",
            name,
            disabled: false,
          };
        },
        delete: async (id: string) => {
          return;
        },
      },
    };
    return user;
  }

  async logIn(creds: Credentials): Promise<any> {
    const app = this;
    console.log("Logging in with credentials:", creds);
    if (creds.type === "anonymous") {
      return await app
        .fetch("/session/create", {
          method: "POST",
          headers: {
            Authorization: "Anonymous",
          },
        })
        .then((res) => res.json())
        .then((customData: UserCustomData) => {
          const user = app.hydrateUser({ customData });
          app.currentUser = user;
          localStorage.setItem(
            `realmCurrentUser:${app.appId}`,
            BSON.EJSON.stringify({ customData }),
          );
          return user;
        });
    } else {
      throw new Error("Invalid credentials type");
    }
  }
}

function mongoClient(app: App) {
  return (name: string) =>
    ({
      db: (dbName: string) => {
        return {
          databaseName: dbName,
          collection: (collName: string) => {
            return new Proxy(
              {
                collectionName: collName,
                // @ts-ignore
                watch: (
                  pipelineDocument?: Document[],
                  options?: ChangeStreamDocument,
                ) => {
                  // console.log(`Watching collection ${collName} in db ${dbName} with options:`, pipelineDocument, options);
                  return {
                    async *[Symbol.asyncIterator]() {
                      // Mocked empty iterator
                    },
                    return: async (value: any) => {
                      // console.log(`Stopped watching collection ${collName} in db ${dbName}`);
                      return { value, done: true };
                    },
                  };
                },
              },
              {
                get(target: any, prop: string) {
                  return async (...args: any) => {
                    return app
                      .fetch(`/dal/query/${dbName}/${collName}/${prop}`, {
                        method: "POST",
                        body: args,
                      })
                      .then((res) => res.json())
                      .then((data) => {
                        // console.log({ dbName, collName, prop, args, data });
                        return data;
                      });
                  };
                },
              },
            );
          },
        };
      },
    }) as Realm.Services.MongoDB;
}
